var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { FeatureFlag } from "healthex-common/features.dtos";
import { computed, observable } from "mobx";
import { determineOrgId, PatientConsentViewModel } from "./PatientConsentViewModel";
import { LoginSource } from "healthex-common/heap.dtos";
import { ClearInitialScreen } from "./ClearAuthManager";
import { Disposer } from "../Infra/Disposer";
import { PatientConsentSessionState } from "./PatientConsentSession";
import { ClearAuthState } from "../Infra/Persistence";
import { isPatientConsentRoute } from "./PatientConsentRoute";
export class PatientConsentClearUpgradeViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.disposer = new Disposer();
        this.isSandboxLogin = false;
        this.hasClearError = false;
        this.isCLEARInformationShown = false;
        this.featureManager = container.featureManager;
        this.clearAuthManager = container.clearAuthManager;
        this.loginUIManager = container.loginUIManager;
        this.persistence = container.persistence;
        this.socialLoginAuthManager = container.socialLoginAuthManager;
        this.isSandboxLogin = this.persistence.isSandboxLogin();
    }
    run() {
        this.disposer.reaction(() => this.patientConsentUIManager.session.patientConsentSessionState, (state) => {
            if (state === PatientConsentSessionState.RESTORING_COMPLETED) {
                this.handleClearLoginResultIfNeeded();
            }
        }, { fireImmediately: true, name: "PatientConsentClearUpgradeViewModel_clearAuthManagerState" });
    }
    stop() {
        this.disposer.dispose();
    }
    get viewToDisplay() {
        var _a;
        if (this.hasClearError)
            return "CLEAR_ERROR";
        if (this.authManager.accountLinkingConflict)
            return "ACCOUNT_LINKING_CONFLICT";
        const isIAL2Expired = (_a = this.patientConsentUIManager.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Expired;
        if (isIAL2Expired === true)
            return "CLEAR_EXPIRED";
        if (isIAL2Expired === null)
            return "LOADING";
        return this.featureManager.isFeatureEnabled(FeatureFlag.CLEAR_FORWARD_UPGRADE) ? "CLEAR_FORWARD" : "FASTEN_FORWARD";
    }
    get flipLongPressLoginButton() {
        return this.isSandboxLogin;
    }
    get sharedWithName() {
        if (this.isIAS) {
            return null;
        }
        else if (this.isAIFlow) {
            return this.aiToolName;
        }
        else {
            return this.organizationName;
        }
    }
    get patientEmail() {
        return this.authManager.patientEmail;
    }
    get isLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get isClearLoginSuccess() {
        return this.clearAuthManager.state === ClearAuthState.SUCCESS;
    }
    async verifyWithClear(isSandbox) {
        if (this.isScreenshotMode)
            return;
        this.patientConsentUIManager.setHasLoggedInDuringConsent();
        this.persistence.setSandboxLogin(isSandbox);
        await this.patientConsentUIManager.saveConsentFlowProgress({ clearUpgradeAttempted: true });
        await this.patientConsentUIManager.loginToClearAndSaveState(window.location.hash, isSandbox, LoginSource.CONSENT_CLEAR_UPGRADE, determineOrgId(this.loginUIManager, this.studyInfo), ClearInitialScreen.PATIENT_CONSENT_CLEAR_UPGRADE);
    }
    async findRecordsMyself() {
        await this.patientConsentUIManager.saveConsentFlowProgress({ clearUpgradeDeclined: true });
        this.goToNextPage();
    }
    showCLEARInformation() {
        this.isCLEARInformationShown = true;
    }
    hideCLEARInformation() {
        this.isCLEARInformationShown = false;
    }
    handleClearLoginResultIfNeeded() {
        var _a;
        if (this.authManager.accountLinkingConflict) {
            this.patientConsentUIManager.completeStateRestoration();
            return;
        }
        const ial2Authorized = (_a = this.patientConsentUIManager.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Authorized;
        if (ial2Authorized === true && this.isClearLoginSuccess) {
            if ((this.patientConsentUIManager.hasCompleteDemographicsFromClear || this.authManager.phoneVerified) &&
                this.socialLoginAuthManager.hasPendingSocialLogin) {
                this.socialLoginAuthManager.clearPendingSocialLogin();
                this.persistence.setConsentDeferredVerificationStash(null);
            }
            const targetPageRoute = this.resolveTargetPageRoute();
            if (targetPageRoute && this.authManager.accountLinkingConflict === null) {
                this.goToNextPageAndResetAuth(targetPageRoute);
            }
        }
        else {
            this.hasClearError = true;
        }
        this.patientConsentUIManager.completeStateRestoration();
    }
    resolveTargetPageRoute() {
        var _a, _b;
        const savedNext = (_b = (_a = this.clearAuthManager.getClearAuthData()) === null || _a === void 0 ? void 0 : _a.nextPageRoute) !== null && _b !== void 0 ? _b : null;
        if (savedNext && isPatientConsentRoute(savedNext) && this.patientConsentUIManager.isRouteInPages(savedNext)) {
            return savedNext;
        }
        const fallback = this.patientConsentUIManager.getResumePageAfterClearLogin();
        return fallback;
    }
    goToNextPageAndResetAuth(nextPageRoute) {
        if (nextPageRoute) {
            this.goToPage(nextPageRoute);
            this.clearAuthManager.resetAuth();
        }
    }
}
__decorate([
    observable
], PatientConsentClearUpgradeViewModel.prototype, "isSandboxLogin", void 0);
__decorate([
    observable
], PatientConsentClearUpgradeViewModel.prototype, "hasClearError", void 0);
__decorate([
    observable
], PatientConsentClearUpgradeViewModel.prototype, "isCLEARInformationShown", void 0);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "viewToDisplay", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "flipLongPressLoginButton", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "sharedWithName", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "patientEmail", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "isLoggingIn", null);
__decorate([
    computed
], PatientConsentClearUpgradeViewModel.prototype, "isClearLoginSuccess", null);
